import { useEffect, useState, useCallback } from 'react';

import { useMasterclass } from '@/contexts/MasterclassContext';
import { supabase } from '@/integrations/supabase/client';
import { captureSentry } from '@/utils/sentryHelper';

import { CertificationStatus } from '../types';

/**
 * Hook to fetch and monitor certification status for a specific case.
 *
 * Certification Access Requirements:
 * 1. Case must be completed (all slides visited)
 * 2. User must have less than 3 attempts
 * 3. User must not have already passed
 *
 * @param caseId - The case ID to check certification status for
 * @param userId - The current user ID
 * @returns {Object} status - Certification status object
 * @returns {boolean} status.canTakeCertification - Whether user can start/retry certification
 * @returns {number} status.attemptsUsed - Number of attempts already used
 * @returns {number} status.maxAttempts - Maximum allowed attempts (3)
 * @returns {number|undefined} status.bestScore - Best certification score achieved
 * @returns {boolean} status.passed - Whether user has passed the certification
 * @returns {boolean} status.caseCompleted - Whether the case is completed
 * @returns {number|undefined} status.caseScore - Current case score percentage
 * @returns {boolean} loading - Whether data is being loaded
 */
export const useCertificationStatus = (caseId: string, userId: string | null) => {
  const [status, setStatus] = useState<CertificationStatus>({
    canTakeCertification: false,
    attemptsUsed: 0,
    maxAttempts: 3,
    passed: false,
    caseCompleted: false,
  });
  const [loading, setLoading] = useState(true);
  const { caseProgress } = useMasterclass();

  const fetchStatus = useCallback(async () => {
    if (!userId) {
      setLoading(false);
      return;
    }

    try {
      // Check completion count from statistics (at least 1 completion required)
      const { data: statsData } = await supabase
        .from('case_statistics')
        .select('completions')
        .eq('user_id', userId)
        .eq('case_id', caseId)
        .maybeSingle();

      const caseCompleted = (statsData?.completions || 0) > 0;

      // Fetch only completed attempts
      const { data: attempts, error } = await supabase
        .from('certification_progress')
        .select('*')
        .eq('user_id', userId)
        .eq('case_id', caseId)
        .not('completed_at', 'is', null)
        .order('attempt_number', { ascending: false });

      if (error) throw error;

      const attemptsUsed = attempts?.length || 0;
      const bestScore =
        attempts?.reduce((max, att) => Math.max(max, att.score || 0), 0) || undefined;
      const passed = attempts?.some((att) => att.passed) || false;
      const canTakeCertification = caseCompleted && attemptsUsed < 3 && !passed;

      setStatus({
        canTakeCertification,
        attemptsUsed,
        maxAttempts: 3,
        bestScore,
        passed,
        caseCompleted,
      });
    } catch (error) {
      console.error('Error fetching certification status:', error);
      captureSentry({
        title: 'Failed to fetch certification status',
        error,
        detail: { caseId, userId },
      });
    } finally {
      setLoading(false);
    }
  }, [userId, caseId]);

  useEffect(() => {
    void fetchStatus();

    // Listen for certification updates
    const handleCertificationUpdate = (event: Event) => {
      const customEvent = event as CustomEvent;
      if (customEvent.detail?.caseId === caseId) {
        void fetchStatus();
      }
    };

    window.addEventListener('certification-updated', handleCertificationUpdate);

    return () => {
      window.removeEventListener('certification-updated', handleCertificationUpdate);
    };
  }, [caseId, userId]);

  return { status, loading, refetch: fetchStatus };
};
